import React, { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MailIcon, MessageCircleIcon, PhoneIcon, Trash2Icon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { addNote, assignLead, deleteLead, deleteNote, updateLeadStage, updateNote } from '../../services/leadService';
import { answerToText, formatDateTime, fullName } from '../../utils/format';
import { PageHeader } from '../../components/crm/PageHeader';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/Tabs';
import { Avatar } from '../../components/ui/Avatar';
export function LeadDetail() {
  const {
    id = ''
  } = useParams();
  const navigate = useNavigate();
  const {
    user,
    members,
    hasPermission
  } = useAuth();
  const {
    leads,
    stages,
    notes,
    history,
    forms,
    landings
  } = useWorkspace();
  const [noteBody, setNoteBody] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingBody, setEditingBody] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const lead = leads.find(l => l.id === id);
  if (!lead) {
    return <div className="rounded-lg border border-border bg-background p-10 text-center" data-id="element-1001" data-mp="33oxu4n1gc2nv">
        <p className="text-sm text-muted-foreground" data-id="element-1002" data-mp="2yq4g8ti969n6">Este lead ya no existe.</p>
        <Link to="/app/leads" className="mt-4 inline-block" data-id="element-1003" data-mp="2yo0f2jbqnoql">
          <Button variant="secondary" data-id="element-1004" data-mp="15vqaqyzc4r">Volver a leads</Button>
        </Link>
      </div>;
  }
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const leadNotes = notes.filter(n => n.leadId === lead.id);
  const leadHistory = history.filter(h => h.leadId === lead.id).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const stage = stages.find(s => s.id === lead.stageId);
  const canEdit = hasPermission('leads.edit');
  const details: {
    label: string;
    value: string;
  }[] = [{
    label: 'Nombre',
    value: lead.firstName
  }, {
    label: 'Apellidos',
    value: lead.lastName
  }, {
    label: 'Empresa',
    value: lead.company
  }, {
    label: 'Email',
    value: lead.email
  }, {
    label: 'Teléfono',
    value: lead.phone
  }, {
    label: 'Página web',
    value: lead.website || '—'
  }, {
    label: 'Fecha de entrada',
    value: formatDateTime(lead.createdAt)
  }, {
    label: 'Fuente',
    value: lead.origin.source
  }, {
    label: 'Campaña',
    value: lead.origin.campaign || '—'
  }, {
    label: 'Landing',
    value: landings.find(l => l.id === lead.origin.landingId)?.name ?? '—'
  }, {
    label: 'Formulario',
    value: forms.find(f => f.id === lead.origin.formId)?.name ?? '—'
  }, {
    label: 'Último contacto',
    value: lead.lastContactAt ? formatDateTime(lead.lastContactAt) : 'Sin contactar'
  }];
  async function handleAddNote() {
    if (!noteBody.trim() || !lead) return;
    setSavingNote(true);
    await addNote(lead.organizationId, lead.id, user?.id ?? '', authorName, noteBody.trim());
    setNoteBody('');
    setSavingNote(false);
  }
  async function handleDelete() {
    if (!lead) return;
    setDeleting(true);
    await deleteLead(lead.id);
    setDeleting(false);
    navigate('/app/leads');
  }
  return <>
      <Link to="/app/leads" className="mb-4 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-id="element-1005" data-mp="1u3uzroymru3">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1006" data-mp="23ms2id8n01fo" />
        Volver a leads
      </Link>

      <PageHeader title={fullName(lead.firstName, lead.lastName)} description={lead.company} actions={<>
            <Button variant="secondary" leftIcon={<MessageCircleIcon className="h-4 w-4" data-id="element-1009" data-mp="2616q7gl7lmby" />} onClick={() => window.open(`https://wa.me/${lead.phone.replace(/\D/g, '')}`, '_blank', 'noopener')} data-id="element-1008" data-mp="211k92w29flxv">
              WhatsApp
            </Button>
            <Button variant="secondary" leftIcon={<MailIcon className="h-4 w-4" data-id="element-1011" data-mp="1md67eogky3iw" />} onClick={() => {
        window.location.href = `mailto:${lead.email}`;
      }} data-id="element-1010" data-mp="1id5k0ebi6hzp">
              Email
            </Button>
            <Button variant="secondary" leftIcon={<PhoneIcon className="h-4 w-4" data-id="element-1013" data-mp="1w53o2aqcxnp0" />} onClick={() => {
        window.location.href = `tel:${lead.phone.replace(/\s/g, '')}`;
      }} data-id="element-1012" data-mp="1w3sfzypvpts3">
              Llamar
            </Button>
            {hasPermission('leads.delete') && <Button variant="destructive" leftIcon={<Trash2Icon className="h-4 w-4" data-id="element-1015" data-mp="r5dsgnges65g" />} onClick={() => setConfirmDelete(true)} data-id="element-1014" data-mp="w2no4kue5e1q">
                Eliminar
              </Button>}
          </>} data-id="element-1007" data-mp="1zl6j091lkowi" />

      <div className="grid gap-5 lg:grid-cols-3" data-id="element-1016" data-mp="i2w4o4l3v5gc">
        {/* Columna principal */}
        <div className="space-y-5 lg:col-span-2" data-id="element-1017" data-mp="d5mhkvl6v5vo">
          <section className="rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1018" data-mp="d6e4zj4mtxav">
            <h2 className="mb-4 text-sm font-semibold text-foreground" data-id="element-1019" data-mp="8brt7eun22q9">
              Datos principales
            </h2>
            <dl className="grid gap-4 sm:grid-cols-2" data-id="element-1020" data-mp="3ulchawj54o0e">
              {details.map(item => <div key={item.label} data-id="element-1021" data-mp="3qjhefnlka2ki">
                  <dt className="text-xs uppercase tracking-wide text-muted-foreground" data-id="element-1022" data-mp="3qi622ze1vmhs">
                    {item.label}
                  </dt>
                  <dd className="mt-1 break-words text-sm text-foreground" data-id="element-1023" data-mp="3lmqswkqzmerp">
                    {item.value}
                  </dd>
                </div>)}
            </dl>
          </section>

          <Tabs defaultValue="respuestas" data-id="element-1024" data-mp="31yku2rtwgn6u">
            <TabsList data-id="element-1025" data-mp="31zmh7uo6x04x">
              <TabsTrigger value="respuestas" data-id="element-1026" data-mp="320fgkaktknd1">Respuestas</TabsTrigger>
              <TabsTrigger value="notas" data-id="element-1027" data-mp="36yqq0k77cvuq">Notas</TabsTrigger>
              <TabsTrigger value="historial" data-id="element-1028" data-mp="3un9wcu6oyyk6">Historial</TabsTrigger>
            </TabsList>

            <TabsContent value="respuestas" data-id="element-1029" data-mp="3upmfhao6reyt">
              {lead.answers.length === 0 ? <p className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground" data-id="element-1030" data-mp="3irzm6oto5xo">
                  Este lead no tiene respuestas asociadas.
                </p> : <dl className="divide-y divide-border rounded-lg border border-border bg-background" data-id="element-1031" data-mp="hg861i74bzpf">
                  {lead.answers.map(answer => <div key={answer.questionId} className="p-4" data-id="element-1032" data-mp="m9jgruf1x0mm">
                      <dt className="text-sm font-medium text-foreground" data-id="element-1033" data-mp="26wu25ue7ggdk">
                        {answer.label}
                      </dt>
                      <dd className="mt-1 text-sm text-muted-foreground" data-id="element-1034" data-mp="21wy6qjjkpx9m">
                        {answerToText(answer.value) || '—'}
                      </dd>
                    </div>)}
                </dl>}
            </TabsContent>

            <TabsContent value="notas" data-id="element-1035" data-mp="1nzsw068x0a81">
              {hasPermission('notes.edit') && <div className="mb-4 rounded-lg border border-border bg-background p-4" data-id="element-1036" data-mp="1j1836rvzsstm">
                  <Textarea id="nueva-nota" label="Nueva nota" rows={3} placeholder="Escribe el resultado de la llamada, próximos pasos…" value={noteBody} onChange={e => setNoteBody(e.target.value)} data-id="element-1037" data-mp="1x0qy2izsm7nz" />
                  <div className="mt-3 flex justify-end" data-id="element-1038" data-mp="33huj96i5cey8">
                    <Button size="sm" loading={savingNote} disabled={!noteBody.trim()} onClick={handleAddNote} data-id="element-1039" data-mp="3nixhmi0hcb0w">
                      Añadir nota
                    </Button>
                  </div>
                </div>}
              {leadNotes.length === 0 ? <p className="rounded-lg border border-dashed border-border bg-surface p-8 text-center text-sm text-muted-foreground" data-id="element-1040" data-mp="a3rhdrakq8lh">
                  Todavía no hay notas para este lead.
                </p> : <ul className="space-y-3" data-id="element-1041" data-mp="k1nqyenv4vhh">
                  {leadNotes.map(note => <li key={note.id} className="rounded-lg border border-border bg-background p-4" data-id="element-1042" data-mp="o1g3t3kahnsn">
                      <div className="flex items-center gap-3" data-id="element-1043" data-mp="2ptkobtzglitc">
                        <Avatar name={note.authorName} size="sm" data-id="element-1044" data-mp="2pw6z02hncefs" />
                        <div className="min-w-0 flex-1" data-id="element-1045" data-mp="2ibd3n80w654h">
                          <p className="text-sm font-medium text-foreground" data-id="element-1046" data-mp="2dk4tg9rqmfq9">
                            {note.authorName}
                          </p>
                          <p className="text-xs text-muted-foreground" data-id="element-1047" data-mp="28l1o0o7ui7hr">
                            {formatDateTime(note.createdAt)}
                          </p>
                        </div>
                      </div>
                      {editingId === note.id ? <div className="mt-3" data-id="element-1048" data-mp="2wbfhmj62weeg">
                          <Textarea id={`edit-${note.id}`} rows={3} value={editingBody} onChange={e => setEditingBody(e.target.value)} data-id="element-1049" data-mp="2w8jjouu8dpq0" />
                          <div className="mt-2 flex justify-end gap-2" data-id="element-1050" data-mp="1gv6v6hks3zoc">
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} data-id="element-1051" data-mp="1bthkx45drq1b">
                              Cancelar
                            </Button>
                            <Button size="sm" onClick={async () => {
                      await updateNote(note.id, editingBody);
                      setEditingId(null);
                    }} data-id="element-1052" data-mp="2weoyjwn5t40z">
                              Guardar
                            </Button>
                          </div>
                        </div> : <>
                          <p className="mt-3 text-sm leading-relaxed text-foreground" data-id="element-1053" data-mp="3bcr7kmcymhpm">
                            {note.body}
                          </p>
                          {note.authorId === user?.id && hasPermission('notes.edit') && <div className="mt-3 flex gap-2" data-id="element-1054" data-mp="1h01da3ioj6hp">
                              <Button size="sm" variant="ghost" onClick={() => {
                      setEditingId(note.id);
                      setEditingBody(note.body);
                    }} data-id="element-1055" data-mp="16v1h1xv7791z">
                                Editar
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteNote(note.id)} data-id="element-1056" data-mp="n83p5dnw5sou">
                                Eliminar
                              </Button>
                            </div>}
                        </>}
                    </li>)}
                </ul>}
            </TabsContent>

            <TabsContent value="historial" data-id="element-1057" data-mp="2b41hfgcsecs3">
              <ol className="space-y-3" data-id="element-1058" data-mp="2b534ykazorqp">
                {leadHistory.map(event => <li key={event.id} className="flex gap-3 rounded-lg border border-border bg-background p-4" data-id="element-1059" data-mp="27157sf585r0a">
                    <span className="mt-1.5 h-2 w-2 flex-none rounded-full bg-primary" data-id="element-1060" data-mp="1x4ac040c64pp" />
                    <div data-id="element-1061" data-mp="2ur96c83k30kf">
                      <p className="text-sm text-foreground" data-id="element-1062" data-mp="2uqhcp2tircws">{event.description}</p>
                      <p className="mt-1 text-xs text-muted-foreground" data-id="element-1063" data-mp="2ps61oorhmwcz">
                        {event.authorName} · {formatDateTime(event.createdAt)}
                      </p>
                    </div>
                  </li>)}
              </ol>
            </TabsContent>
          </Tabs>
        </div>

        {/* Columna lateral */}
        <aside className="space-y-5" data-id="element-1064" data-mp="3dk3dwgyunqem">
          <section className="rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1065" data-mp="38qs4q96kexjj">
            <h2 className="mb-4 text-sm font-semibold text-foreground" data-id="element-1066" data-mp="3g2onp0529zxu">Estado</h2>
            <div className="mb-4" data-id="element-1067" data-mp="3g4sxfypn5t61">
              <StatusBadge status={(stage?.name ?? 'Nuevo') as never} data-id="element-1068" data-mp="3l0q6rl2hlz9b" />
            </div>
            <Select id="estado" label="Cambiar estado" disabled={!canEdit} options={stages.map(s => ({
            value: s.id,
            label: s.name
          }))} value={lead.stageId} onChange={e => updateLeadStage(lead.id, e.target.value, authorName)} data-id="element-1069" data-mp="3q031st3iicmz" />
            <div className="mt-4" data-id="element-1070" data-mp="7toq70spuerr">
              <Select id="comercial" label="Comercial asignado" placeholder="Sin asignar" disabled={!canEdit} options={members.map(m => ({
              value: m.userId,
              label: fullName(m.firstName, m.lastName)
            }))} value={lead.assignedTo ?? ''} onChange={e => {
              const member = members.find(m => m.userId === e.target.value);
              assignLead(lead.id, e.target.value || undefined, member ? fullName(member.firstName, member.lastName) : '', authorName);
            }} data-id="element-1071" data-mp="7sn29vqlpxsm" />
            </div>
          </section>

          <section className="rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1072" data-mp="15bhygdpzhbtt">
            <h2 className="mb-4 text-sm font-semibold text-foreground" data-id="element-1073" data-mp="15dchtnllwvvs">
              Origen del lead
            </h2>
            <dl className="space-y-3 text-sm" data-id="element-1074" data-mp="1eby15kywkexm">
              {[['source', lead.origin.source], ['medium', lead.origin.medium], ['campaign', lead.origin.campaign || '—'], ['content', lead.origin.content || '—'], ['term', lead.origin.term || '—'], ['referrer', lead.origin.referrer || 'directo']].map(([label, value]) => <div key={label} className="flex justify-between gap-3" data-id="element-1075" data-mp="2t8henlgzucw7">
                  <dt className="text-muted-foreground" data-id="element-1076" data-mp="2xbdrevjures8">{label}</dt>
                  <dd className="truncate text-foreground" data-id="element-1077" data-mp="2x8j7d54r3vzc">{value}</dd>
                </div>)}
            </dl>
          </section>
        </aside>
      </div>

      <ConfirmDialog open={confirmDelete} title="Eliminar este lead" description="Se eliminarán también sus notas y su historial. Esta acción no se puede deshacer." loading={deleting} onCancel={() => setConfirmDelete(false)} onConfirm={handleDelete} data-id="element-1078" data-mp="2oapiugd8a6mq" />
    </>;
}